import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';
import '../../styles/components.css';
//import { useSelector } from 'react-redux';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../context/AuthContext';
import MfaVerification from './MfaVerification';
import { getMfaResolver } from '../../services/mfaService';
// Removed Redux auth selectors - now using useAuth() hook

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUserData();
  const { loading } = useAuth();
  
  // MFA state
  const [mfaResolver, setMfaResolver] = useState(null);
  const [showMfaVerification, setShowMfaVerification] = useState(false);

  // Get the intended destination from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (user && !loading) {
      // Small delay to ensure smooth transition and prevent flicker
      const timer = setTimeout(() => {
        navigate(from, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, loading, navigate, from]);

  // Reset submitting state when authentication completes
  useEffect(() => {
    if (user && !loading) {
      setIsSubmitting(false);
    }
  }, [user, loading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent multiple submissions
    
    setIsSubmitting(true);
    try {
      // Sign in with Firebase Auth
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      // Navigation will be handled by useEffect when user and loginComplete are both true
      // This prevents the flicker by not navigating immediately
    } catch (error) {
      console.error('Login error:', error);
      
      // Check if MFA is required
      if (error.code === 'auth/multi-factor-auth-required') {
        // Get the MFA resolver from the error
        const resolver = getMfaResolver(error);
        setMfaResolver(resolver);
        setShowMfaVerification(true);
        setIsSubmitting(false);
        return;
      }
      
      alert(error.message);
      setIsSubmitting(false); // Reset on error
    }
  };

  // Handle MFA verification success
  const handleMfaSuccess = (userCredential) => {
    console.log('MFA verification successful');
    setShowMfaVerification(false);
    setMfaResolver(null);
    // Navigation will be handled by useEffect when user is set
  };

  // Handle MFA verification cancel
  const handleMfaCancel = () => {
    setShowMfaVerification(false);
    setMfaResolver(null);
    setIsSubmitting(false);
  };

  // Show MFA verification screen if required
  if (showMfaVerification && mfaResolver) {
    return (
      <MfaVerification
        resolver={mfaResolver}
        onSuccess={handleMfaSuccess}
        onCancel={handleMfaCancel}
      />
    );
  }

  return (
    <div className="login-page">
      <header className="login-header">
        <img 
          src="/logo.svg" 
          alt="Healthcare EMR" 
          className="login-logo"
        />
      </header>
      
      <div className="login-columns">
        {/* Left Column - Staff Login */}
        <div className="login-column staff-login">
          <div className="login-form">
            <h2 className="form-title">EMR Staff Login</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input 
                  type="email" 
                  id="email"
                  name="email" 
                  className="form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading || isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input 
                  type="password" 
                  id="password"
                  name="password" 
                  className="form-input"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading || isSubmitting}
                />
              </div>
              <button 
                type="submit" 
                className="login-submit-button"
                disabled={loading || isSubmitting}
              >
                {loading || isSubmitting ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
            <div className="login-links">
              <Link to="/forgot-password">Forgot Password?</Link>
              <span className="separator">•</span>
              <Link to="/privacy-policy">Privacy Policy</Link>
            </div>
          </div>
        </div>

        {/* Right Column - Patient Portal */}
        <div className="login-column patient-portal">
          <div className="login-content">
            <h1 className="login-title">
              Manage your healthcare anytime, anywhere with Care360™
            </h1>
            
            <p className="login-description">
              Meet Care360, the free app that allows you to use your mobile device to:
            </p>
            
            <ul className="feature-list">
              <li>Access your personal health information</li>
              <li>Securely message your care team</li>
              <li>View upcoming appointments</li>
              <li>Self-schedule your appointments</li>
              <li>And more</li>
            </ul>

            <div className="app-store-buttons">
              <button 
                type="button" 
                className="app-store-link"
                onClick={() => window.open('https://play.google.com/store/apps', '_blank')}
                aria-label="Get it on Google Play"
              >
                <img src="/google-play-badge.png" alt="Get it on Google Play" />
              </button>
              <button 
                type="button" 
                className="app-store-link"
                onClick={() => window.open('https://apps.apple.com', '_blank')}
                aria-label="Download on the App Store"
              >
                <img src="/app-store-badge.png" alt="Download on the App Store" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer with Privacy Policy Link */}
      <footer className="login-footer">
        <div className="footer-content">
          <Link to="/privacy-policy" className="footer-link">
            Privacy Policy
          </Link>
          <span className="separator">•</span>
          <Link to="/patient-account-deletion" className="footer-link">
            Patient Account Deletion
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Login;
